import type {LandingAttribution} from "analytics/landingVisit";
import {useMessages} from "i18n";
import {ModalSheet, SheetTitleAccent} from "components/ModalSheet";
import {ExitOfferDialog} from "upsell/ExitOfferDialog";
import {ReferralOfferDialog} from "referral/ReferralOfferDialog";
import {useExitOffer} from "upsell/useExitOffer";
import {usePricingPage} from "./usePricingPage";
import type {OfferHost} from "./planPickerPress";
import {BillingPeriod, PlanId} from "./pricingPlans";
import {PricingContent} from "./PricingContent";
import type {OfferTokenSource} from "./offerTokenSource";

/**
 * The pricing dialog (design `.pricing-modal`, ticket #45) — opened from the topbar's "Pricing"
 * link on the single-screen landing. Same view-model ({@link usePricingPage}) and same body
 * ({@link PricingContent}) as the `/pricing` route, in the design's modal sheet.
 *
 * It also HOSTS the exit offer (kitecorp/transferify-ui#1722), and this is the only place that does.
 * `onClose` is the one funnel every dismissal passes through — the ✕, the scrim and Escape all reach
 * it, and so does the plan CTA's own close — so a trigger wired here covers all six mount sites
 * without any of them opting in.
 */

type PricingDialogProps = {
	landingAttribution?: LandingAttribution;
	onTransitionEntered?: () => void;
	open: boolean;
	onClose: () => void;
	/** Optional tier subset for contextual upgrade entry points, such as Plus → Premium. */
	planIds?: PlanId[];
	/** Tier rendered with a disabled "Current plan" action in contextual upgrade flows. */
	currentPlanId?: PlanId;
	/** Card CTA — the tier, the active billing period and the welcome claim token's resolver (see {@link usePricingUpgrade}). */
	onUpgrade: (planId: PlanId, period: BillingPeriod, offerToken?: OfferTokenSource) => void;
	/** Sole local focus-restoration owner when this dialog opens from a disappearing control. */
	onTransitionExited?: () => void;
	/**
	 * Suppresses the exit offer for a caller that is ITSELF an offer (kitecorp/transferify-ui#1722).
	 *
	 * One caller sets it: the first-month offer's tier picker, which stacks this table above itself.
	 * Closing that table returns the reader to an offer dialog still on screen, and a second offer on
	 * top of it is a pile rather than a moment.
	 */
	suppressExitOffer?: boolean;
	/**
	 * The surface that opened this table: a checkout started from it is counted on that surface's funnel,
	 * under its campaign (kitecorp/transferify-ui#2914). The offer it is stacked inside, with
	 * `suppressExitOffer`, or the download-cap card or storage strip that opened the plain table. The
	 * table's own showing and press stay the plan picker's unless it is stacked.
	 */
	host?: OfferHost | null;
};

export function PricingDialog({
	open, onClose, planIds, currentPlanId, onUpgrade, onTransitionExited, onTransitionEntered, landingAttribution, suppressExitOffer = false,
	host = null,
}: PricingDialogProps) {
	const {pricing} = useMessages();
	// `open` is passed on so a CLOSED dialog quotes nobody and asks the api nothing: this component is
	// mounted on routes the reader never opens it from (kitecorp/transferify-ui#1846).
	// `suppressExitOffer` names the one nesting where this table is a tier chooser inside another offer,
	// which is also exactly when it is not a plan-picker showing of its own (ui#1965).
	const {period, plans, comparisonRows, storefront, noteDismissed,
		referral, referralClaim, pressPlanCta, referralIntro, welcome, welcomeOccasion} = usePricingPage(currentPlanId, open, suppressExitOffer, undefined, landingAttribution, undefined, host);
	const exitOffer = useExitOffer(suppressExitOffer, open, storefront, welcome);
	const visiblePlans = planIds ? plans.filter(plan => planIds.includes(plan.id)) : plans;
	const showComparison = !planIds;
	const handlePlanAction = (planId: PlanId, period: BillingPeriod) => {
		// A plan CTA closes this sheet through the same `onClose` as the ✕, so the exit offer has to be
		// told the difference: a reader walking to the till is not a reader leaving. The paid-moment
		// funnel is told the press too, so the verdict that follows can attribute a checkout to this picker.
		// A claim is a walk to the till too, just a different one, so the handoff is noted either way:
		// the reader who pressed a gifted tier is not leaving, and the exit offer must not chase them.
		exitOffer.notePurchaseHandoff();
		pressPlanCta(planId, period, offerToken => onUpgrade(planId, period, offerToken));
	};

	return (
		<>
			<ModalSheet
				open={open}
				onClose={onClose}
				onTransitionExited={onTransitionExited}
				onTransitionEntered={onTransitionEntered}
				// The exit offer decides whether this close was a departure or a handoff to a purchase,
				// and the paid-moment funnel (kitecorp/transferify-ui#1965) reads the same verdict: only a
				// departure is a dismissal of the plan picker. The showing itself is reported by
				// `usePricingPage`, which both hosts of this table share and which owns this report too.
				onClosed={() => {
					if (exitOffer.onPricingClosed() === "departure") {
						noteDismissed();
					}
				}}
				ariaLabel={pricing.dialogAria}
				eyebrow={pricing.eyebrow}
				title={<>{pricing.titleLead}<SheetTitleAccent>{pricing.titleAccent}</SheetTitleAccent></>}
				width={1200}
			>
				<PricingContent
					period={period}
					plans={visiblePlans}
					welcome={welcome}
					welcomeOccasion={welcomeOccasion}
					planHeadingLevel={3}
					comparisonRows={comparisonRows}
					showComparison={showComparison}
					comparisonUsesAncestorScroller={showComparison}
					onUpgrade={handlePlanAction}
					referralIntro={referralIntro}
				/>
			</ModalSheet>
			<ExitOfferDialog open={exitOffer.open} onClose={exitOffer.close} amounts={exitOffer.amounts}/>
			{/* The friend's gift, opened by the swapped CTA rather than by this sheet closing — which is
			    what keeps it and the exit offer from ever being on screen together. */}
			{referral.model !== null && (
				<ReferralOfferDialog
					open={referral.open}
					model={referral.model}
					onClose={referral.close}
					onPaid={referral.closeAfterPaid}
					claim={referralClaim}
				/>
			)}
		</>
	);
}
